import React from 'react';
import { Typography } from '@mui/material';

import { App } from 'BMapsSrc/BMapsApp';
import { Slider } from 'BMapsUI/UIComponents';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import TooltipInfo from 'BMapsSrc/ui/common/TooltipInfo';
import { BestPoseBindingInfo } from 'BMapsModel/FragmentSearchResult';
import { Length3, Sub3_3 } from 'BMapsSrc/math';
import { CoralComponent } from './CoralComponent';
import LinkLikeButton from '../../common/LinkLikeButton';
import { AnnotatedSearchResult } from './AnnotatedSearchResult';
import { svgImgSrc } from '../../ui_utils';
import { renderDebug, getMapCaseAndTargetInfo, getKinaseIdForMapCase } from './utils';
import {
    getColumnInfo, getValue,
    ActiveTargetColor, ActiveOffTargetColor,
} from './column_definitions';

/**
 * Component to display a fragment search result table with header and data rows.
 */
export function SuggestionTable({
    uiInfo, sortedSuggestions, columns,
    sortCol, sortAscending, handleSort,
    showImages, toggleImages,
    suggestionAccept, suggestionEnter, suggestionExit,
    cycleSuggestions,
    isPinned, togglePin,
    activityInfo,
    sortPosesBy, handleSortPosesBy, allDisplayFilters, handleUpdateDisplayFilters,
}) {
    const projectCases = AnnotatedSearchResult.getProjectCasesForSuggestions(sortedSuggestions);

    return (
        <table>
            <thead>
                <SuggestionsHeader
                    columns={columns}
                    sortCol={sortCol}
                    sortAscending={sortAscending}
                    handleSort={handleSort}
                    showImages={showImages}
                    toggleImages={toggleImages}
                />
            </thead>
            <tbody>
                { sortedSuggestions.map((d) => (
                    <SuggestionRow
                        key={d.index}
                        sugg={d}
                        uiInfo={uiInfo}
                        columns={columns}
                        showImg={showImages}
                        suggestionAccept={suggestionAccept}
                        suggestionEnter={suggestionEnter}
                        suggestionExit={suggestionExit}
                        onChangePose={cycleSuggestions}
                        pinned={isPinned(d)}
                        togglePin={togglePin}
                        projectCases={projectCases}
                        activityInfo={activityInfo}
                        sortPosesBy={sortPosesBy}
                        handleSortPosesBy={handleSortPosesBy}
                        displayFilters={allDisplayFilters[d.name]}
                        handleUpdateDisplayFilters={handleUpdateDisplayFilters}
                    />
                ))}
            </tbody>
        </table>
    );
}
/**
 * Component to display one row of a fragment search result table.
 * @description props = {
 *      sugg: suggestion object,
 *      showImg: whether to show images instead of fragment names,
 *      uiInfo: labels and presence of certain controls, based on command
 *      suggestionAccept, suggestionEnter, suggestionAccept: callbacks
 * }
 */
class SuggestionRow extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = { sliderExpanded: false };

        this.toggle = this.toggle.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    onChange(valueIn) {
        const { sugg, onChangePose } = this.props;
        const value = Number(valueIn);
        onChangePose(sugg, value);
        this.forceUpdate();
    }

    getEltId() {
        const { sugg } = this.props;
        return `suggestion-${sugg.index}`;
    }

    /**
     * Return extra info to append to suggestion row tooltip. Currently just min b-values.
     * @returns {string} string to append.  Embedded html is ok.
     */
    getTooltipExtras() {
        // Note: this projectCases includes projectCases from all suggestions,
        // not just this one. It can't necessarily be pulled from the suggestion unless
        // we guarantee that all suggestions always have all projectCases.
        const { sugg, projectCases } = this.props;

        const fragInfos = projectCases.map((projectCase) => {
            const fragInfo = App.Workspace.getInfoForFragment(sugg.name, projectCase);
            return {
                projectCase,
                fragInfo,
                minBValue: fragInfo != null ? fragInfo.minBValue : 'N/A',
            };
        });

        const ret = [];
        if (fragInfos.length === 1) {
            ret.push(`Min B-Value: ${fragInfos[0].minBValue}`);
        } else if (fragInfos.length > 1) {
            ret.push('Min B-Values:');
            for (const { projectCase, minBValue } of fragInfos) {
                ret.push(`${projectCase}: ${minBValue}`);
            }
        }
        return ret;
    }

    toggle() {
        this.setState((old) => {
            const { sugg, suggestionExit } = this.props;
            if (old.sliderExpanded) suggestionExit(sugg);
            return { sliderExpanded: !old.sliderExpanded };
        });
    }

    render() {
        if (renderDebug) console.log(`Rendering SuggestionRow ${this.getEltId()}`);
        const {
            sugg, showImg, uiInfo, columns,
            suggestionAccept, suggestionEnter, suggestionExit,
            pinned, togglePin,
            activityInfo, projectCases,
            sortPosesBy, handleSortPosesBy, displayFilters, handleUpdateDisplayFilters,
        } = this.props;
        const { sliderExpanded } = this.state;

        const eltId = this.getEltId();
        const altText = `2D diagram of ${sugg.name}`;
        const imgTooltip = (<img alt={altText} className="suggestion-imgTooltip" src={svgImgSrc(sugg.img)} />);
        const tooltip = showImg ? sugg.name : imgTooltip;
        const tooltipExtras = this.getTooltipExtras();

        const addOnRowClick = !uiInfo.usingSlider;
        const accept = () => suggestionAccept(sugg);
        const addButton = (
            <BMapsTooltip
                placement="top"
                title={sugg.selectedFrag ? uiInfo.addButtonTitle : 'No poses available. Clear filters to see more fragments.'}
            >
                <LinkLikeButton
                    style={{ color: 'var(--marketing-blue)' }}
                    onClick={(event) => { event.stopPropagation(); accept(); }}
                    disabled={!sugg.selectedFrag}
                >
                    <i className="fa fa-plus-square-o" />
                </LinkLikeButton>
            </BMapsTooltip>
        );
        const pin = (
            <BMapsTooltip
                placement="top"
                title={`Keep viewing ${sugg.displayPoses.length > 1 ? 'these poses' : 'this pose'} in the workspace`}
            >
                <LinkLikeButton
                    onClick={(event) => { event.stopPropagation(); togglePin(sugg); }}
                    style={{ color: pinned ? 'var(--marketing-blue)' : 'lightgrey' }}
                >
                    <i className="fa fa-thumb-tack" />
                </LinkLikeButton>
            </BMapsTooltip>
        );
        const toggleButton = (
            <BMapsTooltip
                placement="right"
                // \xa0 is non-breaking space
                title={sliderExpanded ? 'Hide pose explorer' : `Show pose explorer (${sugg.frags.length}\xa0${sugg.frags.length === 1 ? 'pose' : 'poses'}\xa0available)`}
                // tooltip can interfere with dropdown button, so move it right a little.
                slotProps={{
                    popper: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 6],
                                },
                            },
                        ],
                    },
                }}
            >
                <LinkLikeButton
                    onClick={this.toggle}
                    className="toggle"
                    style={{ color: 'var(--marketing-blue)' }}
                >
                    {sliderExpanded
                        ? <i className="fa fa-caret-down" />
                        : <i className="fa fa-caret-left" /> }
                </LinkLikeButton>
            </BMapsTooltip>
        );

        return (
            <>
                <tr
                    key={eltId}
                    className="suggestionRow"
                    id={eltId}
                    onMouseEnter={() => suggestionEnter(sugg)}
                    onMouseLeave={() => suggestionExit(sugg)}
                    onClick={() => addOnRowClick && accept()}
                >
                    { columns.map((col, i) => (
                        <SuggestionRowCell
                            key={`${eltId}+${col}`}
                            suggestion={sugg}
                            col={col}
                            index={i}
                            activityInfo={activityInfo}
                            tooltip={col === 'name' || col === 'svg' ? tooltip : undefined}
                        />
                    ))}
                    <td>
                        {pin}
                        {addButton}
                        {uiInfo.usingSlider && toggleButton}
                    </td>
                </tr>
                {uiInfo.usingSlider && (
                <tr
                    key={`slider-${sugg.index}`}
                    onMouseEnter={() => suggestionEnter(sugg)}
                    onMouseLeave={() => suggestionExit(sugg)}
                >
                    <td colSpan="100">
                        {sliderExpanded && (
                            <SuggestionPoseDetail
                                onChange={this.onChange}
                                sugg={sugg}
                                projectCases={projectCases}
                                sortPosesBy={sortPosesBy}
                                handleSortPosesBy={handleSortPosesBy}
                                displayFilters={displayFilters}
                                handleUpdateDisplayFilters={handleUpdateDisplayFilters}
                                toggle={this.toggle}
                            />
                        )}
                    </td>
                </tr>
                )}
            </>
        );
    }
}

function SuggestionPoseDetail({
    onChange, sugg, projectCases,
    sortPosesBy, handleSortPosesBy, displayFilters, handleUpdateDisplayFilters,
    toggle,
}) {
    const sliderValue = sugg.selectedDisplayPose?.displayIndex ?? -1;
    const poses = sugg.displayPoses;
    const filterValue = (propInfo) => displayFilters?.[propInfo.id]?.value ?? '';
    const setFilterValue = (propInfo, value) => {
        let entry;
        if (numberOrTruthy(value)) {
            entry = {
                value: propInfo.precision != null ? Number(value) : value,
                filter: propInfo.precision != null
                    ? (frag) => {
                        // Round pose value so if the user enters the visible min, it always passes.
                        const poseValue = posePropValue(sugg, frag, propInfo);
                        const factor = 10 ** propInfo.precision;
                        const roundedPoseValue = Math.round(poseValue * factor) / factor;
                        return roundedPoseValue <= value;
                    }
                    : (frag) => posePropValue(sugg, frag, propInfo) === value,
            };
        }
        handleUpdateDisplayFilters({ [propInfo.id]: entry }, { sugg });
    };
    function clearFilters() {
        const filterObj = { };
        for (const prop of posePropsOfInterest()) {
            filterObj[prop.id] = null;
        }
        handleUpdateDisplayFilters(filterObj, { sugg });
    }
    const activeFilters = Object.values(displayFilters || {})
        .filter((filter) => numberOrTruthy(filter.value)).length;

    const totalPoseCount = sugg.frags.length;
    const visiblePoseCount = poses.length;
    let poseLabel = <i>{`No poses match the specified filter${activeFilters > 1 ? 's' : ''} (${totalPoseCount} total poses available)`}</i>;
    if (visiblePoseCount > 0) {
        // \xa0 is non-breaking space
        poseLabel = `Highlighting pose\xa0${1 + sliderValue}\xa0/\xa0${visiblePoseCount}`;
        if (visiblePoseCount !== totalPoseCount) {
            poseLabel += ` (filtered from ${totalPoseCount} total)`;
        }
    }
    const multiProtein = projectCases.length > 1;

    return (
        <Typography
            component="div"
            className="suggestionPoseDetail"
        >
            <BMapsTooltip title="Hide pose explorer" placement="right">
                <LinkLikeButton
                    className="ligand_mod_close"
                    onClick={toggle}
                    style={{ color: 'black', padding: 0, lineHeight: 0 }}
                >
                    <i className="fa fa-close" />
                </LinkLikeButton>
            </BMapsTooltip>
            <div>{poseLabel}</div>
            {visiblePoseCount > 1 && (
                <div className="suggestionPoseDetailHeader">
                    <Slider
                        eltId={`suggestion-${sugg.index}-slider`}
                        value={sliderValue}
                        max={poses.length-1}
                        onChange={onChange}
                    />
                    Sort:
                    <select
                        value={sortPosesBy}
                        onChange={(e) => handleSortPosesBy(e.target.value, { sugg })}
                        style={{ fontSize: 'inherit' }}
                    >
                        {
                            posePropsOfInterest()
                                .filter((propInfo) => multiProtein || !propInfo.multiProteinOnly)
                                .map((propInfo) => (
                                    <option key={propInfo.id} value={propInfo.id}>
                                        {propInfo.selectLabel || propInfo.label}
                                    </option>
                                ))
                        }
                    </select>
                    <TooltipInfo
                        placement="right"
                        title="Use the slider to highlight and view properties for specific poses in the visible distribution, which is organized according to the specified sort and filters."
                        sx={{ fontSize: '1rem' }}
                    />
                </div>
            )}
            <table style={{ marginBottom: 0 }}>
                <thead>
                    <tr>
                        <th style={{ textAlign: 'left' }}>
                            <BMapsTooltip
                                title="Properties of poses in the visible distribution for this fragment"
                                placement="top"
                            >
                                Property
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Property value for the highlighted pose"
                                placement="top"
                            >
                                Value
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Minimum property value across the *visible* distribution of poses for this fragment"
                                placement="top"
                            >
                                Min
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Average property value across the *visible* distribution of poses for this fragment"
                                placement="top"
                            >
                                Mean
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Maximum property value across the *visible* distribution of poses for this fragment"
                                placement="top"
                            >
                                Max
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Filter the visible distribution of poses for this fragment. Numeric filters specify the inclusive maximum."
                                placement="top"
                            >
                                Filter (Max)
                            </BMapsTooltip>
                            <BMapsTooltip placement="right" title="Remove all filters">
                                <LinkLikeButton
                                    onClick={() => clearFilters()}
                                    style={{
                                        visibility: activeFilters > 0 ? 'visible' : 'hidden',
                                        color: 'var(--marketing-blue)',
                                    }}
                                >
                                    <i className="fa fa-times" />
                                </LinkLikeButton>
                            </BMapsTooltip>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {getPoseDetails(sugg, sliderValue)
                        .filter(([prop]) => multiProtein || !prop.multiProteinOnly)
                        .filter(([prop, value]) => (
                            numberOrTruthy(value)
                            || numberOrTruthy(displayFilters?.[prop.id]?.value)
                        ))
                        .map(
                            ([prop, value, stats]) => (
                                <tr key={prop.id}>
                                    <td>
                                        <div style={{ justifyContent: 'start' }}>{prop.label}</div>
                                    </td>
                                    <td>
                                        <div>{value ?? ''}</div>
                                    </td>
                                    <td>
                                        <div>{stats?.min?.toFixed(1) ?? ''}</div>
                                    </td>
                                    <td>
                                        <div>{stats?.avg?.toFixed(1) ?? ''}</div>
                                    </td>
                                    <td>
                                        <div>{stats?.max?.toFixed(1) ?? ''}</div>
                                    </td>
                                    <td>
                                        <div style={{ paddingLeft: '1.5em' }}>
                                            {getPosePropFilter(
                                                prop, sugg, filterValue(prop), setFilterValue
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )
                        )}
                </tbody>
            </table>
        </Typography>
    );
}

/**
 * Component to display the header of a fragment search result table.
 */
function SuggestionsHeader({
    columns,
    sortCol, sortAscending, handleSort,
    toggleImages,
}) {
    return (
        <>
            {/* grouphead displays protein labels in multi-protein case */}
            <SuggestionGroupHeadRow columns={columns} />
            {/* colhead displays the sortable column headers */}
            <tr key="header-row" className="ligandmod-colhead">
                {/* Frag/Name toggle column */}
                <th
                    style={{ width: '1px' }}
                    onClick={() => { toggleImages(); }}
                    title="Switch between fragment images and names"
                >
                    <i id="image-name-toggle" className="fa fa-random" />
                </th>
                {/* Columns to display */}
                { columns.map((col) => (
                    <SuggestionColumnHead
                        key={col}
                        col={col}
                        sortCol={sortCol}
                        sortAscending={sortAscending}
                        handleSort={handleSort}
                    />
                ))}
                {/* Controls column */}
                <th aria-label="Controls"> </th>
            </tr>
        </>
    );
}

/**
 * @param {AnnotatedSearchResult} sugg
 */
function getInfoForCoralMap(sugg) {
    return Object.values(sugg.perCaseInfo).map((projectCaseEntry) => ({
        kinaseId: getKinaseIdForMapCase(projectCaseEntry.mapCase),
        projectCase: projectCaseEntry.projectCase,
        mapCase: projectCaseEntry.mapCase,
        targetInfo: projectCaseEntry.targetInfo,
        fragInfo: sugg.fragInfo,
        bindingScore: projectCaseEntry.bestPoseInfo?.bindingScore,
        ligandEfficiency: projectCaseEntry.bestPoseInfo?.ligandEfficiency,
        enSolute: projectCaseEntry.bestPoseInfo?.enSolute,
        ligandEfficiencyEn: projectCaseEntry.bestPoseInfo?.ligandEfficiencyEn,
    }));
}

function SuggestionColumnHead({
    col, handleSort, sortCol, sortAscending,
}) {
    const { properties: { noSort, sortKey, table } } = getColumnInfo(col) || {};
    const { header, title, suffix } = table || {};

    const sortIcon = (c) => {
        if (c === sortCol) {
            const className = sortAscending ? 'fa fa-caret-up' : 'fa fa-caret-down';
            return <i className={className} style={{ display: 'inline' }} />;
        } else {
            return '';
        }
    };

    return (
        <th
            onClick={!noSort ? () => handleSort(sortKey || col) : undefined}
            title={title}
        >
            {header}
            {!noSort && (
                <>
                    &nbsp;
                    {sortIcon(col)}
                </>
            )}
            {!!suffix && suffix}
        </th>
    );
}

/**
 * Display the GroupHead header row for the SuggestionTable.
 * In the multi-protein case, display the project case names with tooltips with target info.
 */
function SuggestionGroupHeadRow({ columns }) {
    // Organize columns by groups according to their associated project case, so it can
    // create one <th> with a colSpan for the whole group.
    // <th>s for groups associated with a projectCase will have tooltips with target info.
    // This is perhaps unnecessarily complex because it avoids making assumptions about
    // column structure. This means it could handle something like:
    //     Col1, Col2, Col3_pcA, Col3_pcB, Col4, Col5_pcA, Col6_pcA, Col5_pcB, Col6_pcB
    // Resulting groups:
    //     [Col1, Col2], [Col3_A], {Col3_B], [Col4], [Col5_A, Col6_A], [Col5_B, Col6_B]

    const getColumnGroupEntry = (projectCase, cols) => {
        const { mapCase, targetInfo } = getMapCaseAndTargetInfo(projectCase);
        const target = !(targetInfo?.isTarget) ? 'Off-target' : 'Target';
        return {
            shortName: mapCase?.getShortName(),
            title: targetInfo ? `${target}: ${mapCase?.getLongName()}` : undefined,
            cols: [...cols], // Make a copy
        };
    };

    const columnGroups = [];
    const allProjectCases = new Set();
    let workingProjectCase;
    let workingColumnGroup = [];

    for (const col of columns) {
        const { projectCase } = getColumnInfo(col);
        if (projectCase) allProjectCases.add(projectCase);
        if (projectCase !== workingProjectCase) {
            columnGroups.push(getColumnGroupEntry(workingProjectCase, workingColumnGroup));
            workingColumnGroup = [];
            workingProjectCase = projectCase;
        }
        workingColumnGroup.push(col);
    }

    columnGroups.push(getColumnGroupEntry(workingProjectCase, workingColumnGroup));

    return allProjectCases.size > 1 && (
        <tr className="ligandmod-grouphead">
            {/* Frag/Name */}
            <th colSpan="1"> </th>
            {/* Columns */}
            {columnGroups.map(({
                cols, shortName, title,
            }, i) => (
                <th
                    key={`groupheadCol_${i.toString()}`}
                    colSpan={cols.length}
                    title={title}
                >
                    {shortName || ' '}
                </th>
            ))}
            {/* Controls */}
            <th> </th>
        </tr>
    );
}

function SuggestionRowCell({
    suggestion, col, index, activityInfo, tooltip,
}) {
    const colInfo = getColumnInfo(col);
    if (!colInfo) return null;
    const { projectCase } = colInfo;
    const { highlightActiveFrags, activityThresholdInfo: threshInfo } = activityInfo;

    const isFirstCol = index === 0;
    const colSpan = isFirstCol ? 2 : undefined;
    const defaultStyle = { textAlign: 'right', paddingRight: '20px' };
    const style = { ...defaultStyle };
    if (projectCase && highlightActiveFrags) {
        const perCaseInfo = suggestion.perCaseInfo[projectCase];
        if (perCaseInfo.isActiveOffTarget(threshInfo)) style.backgroundColor = ActiveOffTargetColor;
        if (perCaseInfo.isActiveTarget(threshInfo)) style.backgroundColor = ActiveTargetColor;
    }

    return (
        <BMapsTooltip placement="top" title={tooltip}>
            <td
                style={style}
                colSpan={colSpan}
            >
                { getTableValue(suggestion, col) }
            </td>
        </BMapsTooltip>
    );
}

function getTableValue(suggestion, col) {
    const val = getValue(suggestion, col);
    const { column, properties: { table } } = getColumnInfo(col);
    const { precision, getValue: getMyValue } = table || {};
    switch (true) {
        case precision != null:
            return val?.toFixed(precision) ?? '';
        case getMyValue != null:
            return getMyValue(val);
        case column === 'coralMap':
            return <CoralComponent coralMapInfoEntries={getInfoForCoralMap(suggestion)} />;
        case column === 'svg': {
            const altText = `2D diagram of ${suggestion.name}`;
            return <img alt={altText} width="70px" src={svgImgSrc(suggestion.img)} />;
        }
        default:
            return val;
    }
}

function getPoseDetails(sugg, displayIndex) {
    const currentPose = sugg.getFragByDisplayIndex(displayIndex);
    const poseStats = getPoseStats(sugg);
    const entries = posePropsOfInterest()
        .filter((propInfo) => !propInfo.excludeFromTable)
        .map((propInfo) => [
            propInfo,
            currentPose ? poseDisplayValue(sugg, currentPose, propInfo) : null,
            poseStats[propInfo.id],
        ]);
    // Sort the property listing so that projectCase (Structure) is always first
    entries.sort(([a], [b]) => {
        switch (true) {
            case a.id === 'projectCase' && b.id !== 'projectCase': return -1;
            case a.id !== 'projectCase' && b.id === 'projectCase': return 1;
            default: return 0;
        }
    });
    return entries;
}

function getPoseStats(sugg) {
    const poses = sugg.displayPoses.map((pose) => pose.frag);
    if (poses.length === 0) return {};

    return posePropsOfInterest().reduce((acc, nextPropInfo) => {
        const { id: propId, precision } = nextPropInfo;
        if (precision == null) return acc; // not a number, can't do stats
        const stats = {
            min: null, max: null, avg: null, summary: null,
        };
        let total = 0;
        for (const pose of poses) {
            const value = posePropValue(sugg, pose, nextPropInfo);
            if (stats.min == null || value < stats.min) stats.min = value;
            if (stats.max == null || value > stats.max) stats.max = value;
            total += value;
        }
        stats.avg = total / poses.length;
        stats.summary = `Min: ${stats.min?.toFixed(1) || 'N/A'}; Max: ${stats.max?.toFixed(1) || 'N/A'}; Avg: ${stats.avg?.toFixed(1) || 'N/A'}`;
        acc[propId] = stats;
        return acc;
    }, {});
}

function posePropsOfInterest() {
    return Object.values({
        position: {
            id: 'position',
            label: 'Position',
            precision: 1,
            excludeFromTable: true,
            calcValue(sugg, pose) {
                let value;
                const refFrag = sugg.displayPoses[0]?.frag;
                if (refFrag) {
                    const dist = Length3(Sub3_3(refFrag.translation, pose.translation));
                    value = Math.sqrt(dist);
                    value = dist;
                }
                return value;
            },
        },
        bindingScore: {
            id: 'bindingScore',
            label: 'Binding Score',
            precision: 1,
            calcValue(_, pose) {
                return BestPoseBindingInfo.getBindingScore(pose.exchemPotential);
            },
        },
        enSolute: {
            id: 'enSolute',
            label: 'Interaction Energy',
            precision: 1,
        },
        enSolv: {
            id: 'enSolv',
            label: (
                <>
                    &Delta;&Delta;G
                    <sub>s</sub>
                </>
            ),
            selectLabel: <>&Delta;&Delta;Gs</>,
            precision: 1,
        },
        exchemPotential: {
            id: 'exchemPotential',
            label: 'Ex. Chem. Potential',
            precision: 1,
        },
        projectCase: {
            id: 'projectCase',
            label: 'Structure',
            precision: null,
            multiProteinOnly: true,
            displayValue(value) {
                return projectCaseDisplay(value);
            },
            getFilterInput: (filterValue, setFilterValue, sugg) => (
                <select
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                >
                    <option value="">{filterValue !== '' ? 'All' : ''}</option>
                    { sugg.projectCases.map((pc) => {
                        const count = sugg.frags.filter((frag) => frag.projectCase === pc).length;
                        return (
                            <option
                                key={pc}
                                value={pc}
                                disabled={count === 0}
                            >
                                {projectCaseDisplay(pc)}
                                {filterValue !== pc ? ` (${count})` : ''}
                            </option>
                        );
                    })}
                </select>
            ),
        },
    });
}

function posePropValue(sugg, pose, propInfo) {
    const { id: propId, calcValue } = propInfo;
    const value = calcValue ? calcValue(sugg, pose) : pose[propId];
    return value;
}

function poseDisplayValue(sugg, pose, propInfo) {
    const { displayValue, precision } = propInfo;
    const value = posePropValue(sugg, pose, propInfo);
    if (displayValue) return displayValue(value);
    return (precision == null ? value : value?.toFixed(precision));
}

function getPosePropFilter(propInfo, sugg, filterValue, setFilterValue) {
    const { getFilterInput } = propInfo;
    let input;
    if (getFilterInput) {
        input = getFilterInput(filterValue, (val) => setFilterValue(propInfo, val), sugg);
    } else if (propInfo.precision != null) {
        input = (
            <input
                type="number"
                step="0.5"
                value={filterValue}
                onChange={(e) => setFilterValue(propInfo, e.target.value)}
            />
        );
    }

    return (!!input && (
        <>
            {input}
            <BMapsTooltip placement="right" title="Remove filter">
                <LinkLikeButton
                    onClick={() => setFilterValue(propInfo, null)}
                    style={{
                        visibility: numberOrTruthy(filterValue) ? 'visible' : 'hidden',
                        color: 'var(--marketing-blue)',
                    }}
                >
                    <i className="fa fa-times" />
                </LinkLikeButton>
            </BMapsTooltip>
        </>
    ));
}

function numberOrTruthy(value) {
    return typeof value === 'number' || !!value;
}

function projectCaseDisplay(projectCase) {
    const info = getMapCaseAndTargetInfo(projectCase);
    return info.mapCase.getShortName();
}
